import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { brand, navLinks } from '@/mocks/site';
import { briefs } from '@/mocks/topics';

export default function SiteHeader() {
  const [menuOpen, setMenuOpen] = useState(false);
  const [keyword, setKeyword] = useState('');
  const navigate = useNavigate();

  const handleSearch = (e: FormEvent) => {
    e.preventDefault();
    const q = keyword.trim();
    navigate(q ? `/feed?q=${encodeURIComponent(q)}` : '/feed');
    setMenuOpen(false);
  };

  return (
    <header className="sticky top-0 z-50">
      <div className="bg-foreground-950 text-background-100">
        <div className="px-4 md:px-6 h-9 flex items-center gap-3 overflow-hidden">
          <span className="shrink-0 flex items-center gap-1.5 text-[11px] font-label tracking-widest text-accent-400">
            <span className="w-1.5 h-1.5 rounded-full bg-primary-500" />
            线报快讯
          </span>
          <div className="relative flex-1 overflow-hidden">
            <div className="ticker-track flex items-center gap-10 whitespace-nowrap w-max">
              {[...briefs, ...briefs].map((item, index) => (
                <span key={`${item.time}-${index}`} className="text-xs text-background-200">
                  <span className="text-accent-400 mr-1.5">{item.time}</span>
                  {item.text}
                </span>
              ))}
            </div>
          </div>
        </div>
      </div>

      <div className="bg-background-50/95 backdrop-blur border-b border-background-200/80">
        <div className="px-4 md:px-6 h-16 flex items-center justify-between gap-4">
          <Link to="/" className="flex items-center gap-2.5 shrink-0">
            <span className="w-9 h-9 rounded-md bg-primary-500 text-background-50 flex items-center justify-center font-heading text-lg font-bold">
              揭
            </span>
            <span className="flex flex-col leading-none">
              <strong className="font-heading text-lg font-bold text-foreground-950">{brand.name}</strong>
              <span className="hidden sm:block text-[10px] text-foreground-500 tracking-[0.2em] mt-0.5">
                独家线报 · 图解内幕
              </span>
            </span>
          </Link>

          <nav aria-label="主导航" className="hidden md:flex items-center gap-7">
            {navLinks.map((link) => (
              <Link
                key={link.to}
                to={link.to}
                className="text-sm font-medium text-foreground-800 hover:text-primary-600 transition-colors"
              >
                {link.label}
              </Link>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <form onSubmit={handleSearch} role="search" className="hidden lg:flex items-center gap-2 bg-background-100 border border-background-200 rounded-full px-3.5 h-9 w-56">
              <i className="ri-search-line text-sm text-foreground-500" />
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索线报 / 事件"
                aria-label="搜索线报"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 outline-none"
              />
            </form>
            <Link
              to="/#subscribe"
              className="hidden sm:flex items-center gap-1.5 h-9 px-4 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap cursor-pointer hover:bg-primary-600 transition-colors"
            >
              <i className="ri-mail-send-line" />
              订阅线报
            </Link>
            <button
              type="button"
              aria-label="展开菜单"
              aria-expanded={menuOpen}
              onClick={() => setMenuOpen((v) => !v)}
              className="md:hidden w-9 h-9 rounded-md border border-background-200 flex items-center justify-center text-foreground-800 cursor-pointer"
            >
              <i className={menuOpen ? 'ri-close-line text-xl' : 'ri-menu-line text-xl'} />
            </button>
          </div>
        </div>

        {menuOpen && (
          <div className="md:hidden border-t border-background-200 bg-background-50 px-4 py-4 space-y-4">
            <form onSubmit={handleSearch} role="search" className="flex items-center gap-2 bg-background-100 border border-background-200 rounded-full px-3.5 h-10">
              <i className="ri-search-line text-sm text-foreground-500" />
              <input
                type="search"
                name="q"
                value={keyword}
                onChange={(e) => setKeyword(e.target.value)}
                placeholder="搜索线报 / 事件"
                aria-label="搜索线报"
                className="w-full bg-transparent text-sm text-foreground-900 placeholder:text-foreground-500 outline-none"
              />
            </form>
            <nav aria-label="移动端导航" className="flex flex-col">
              {navLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  onClick={() => setMenuOpen(false)}
                  className="py-3 border-b border-background-200/70 text-sm font-medium text-foreground-800"
                >
                  {link.label}
                </Link>
              ))}
            </nav>
            <Link
              to="/#subscribe"
              onClick={() => setMenuOpen(false)}
              className="flex items-center justify-center gap-1.5 h-10 rounded-full bg-primary-500 text-background-50 text-sm font-medium whitespace-nowrap"
            >
              <i className="ri-mail-send-line" />
              订阅线报
            </Link>
          </div>
        )}
      </div>
    </header>
  );
}